import { startTransition, useEffect, useEffectEvent, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { TemplateCodeParameter } from "../ars_ldm";
import { PATCH_ARS_STATE, useArsContext } from "../contexts";
import { isAnalysisProgrammingCodeTemplate } from "../typeGuards";
import { TemplateCodeParameterControl } from "./TemplateCodeParameterControl";

export interface AnalysisMethodParametersControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisMethod in ArsContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
}

/**
 * A component for managing the parameters of an AnalysisMethod.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisMethod in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the analysis method parameters editor.
 */
export const AnalysisMethodParametersControl = ({
  dataPath,
  disabled = false,
}: AnalysisMethodParametersControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const codeTemplate = getFromDataPath(dataPath, state, isAnalysisProgrammingCodeTemplate);
  const parameters = codeTemplate?.parameters ?? [];
  const [activeTab, setActiveTab] = useState<string>(parameters.length > 0 ? "parameter-0" : "");

  /**
   * Handles adding a template code parameter.
   */
  const handleAddParameter = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && codeTemplate) {
      const parameterCount = parameters.length;
      const newParameter: TemplateCodeParameter = {
        name: `Parameter_${parameterCount + 1}`,
        label: `Parameter ${parameterCount + 1}`,
        description: "",
        valueSource: "operation",
        value: [],
        parameterType: "string",
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/parameters/${parameterCount}`,
        value: newParameter,
      });
    }
  };

  /**
   * Event handler to update the active tab.
   *
   * @param newTab - Tab key to activate.
   */
  const updateActiveTab = useEffectEvent((newTab: string) =>
    startTransition(() => setActiveTab(newTab)),
  );

  /** Move tabs */
  useEffect(() => {
    if (parseInt(activeTab.split("-")[1], 10) >= parameters.length) {
      updateActiveTab(parameters.length > 0 ? "parameter-0" : "");
    } else if (activeTab === "" && parameters.length > 0) {
      updateActiveTab("parameter-0");
    }
  }, [parameters.length, activeTab]);

  const idRoute = dataPath.replace(/\//g, "-") + "-parameters";

  return (
    <Tab.Container
      activeKey={activeTab}
      onSelect={(k) => {
        /* istanbul ignore else */
        if (k) {
          setActiveTab(k);
        }
      }}
    >
      <Nav
        variant="tabs"
        className="mb-3"
      >
        {parameters.map((parameter, index) => (
          <Nav.Item key={index}>
            <Nav.Link eventKey={`parameter-${index}`}>{parameter.name}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <Tab.Content>
        {parameters.map((parameter, index) => (
          <Tab.Pane
            eventKey={`parameter-${index}`}
            key={index}
          >
            <TemplateCodeParameterControl
              key={`${parameter.name}-${index}`}
              dataPath={`${dataPath}/parameters/${index}`}
              disabled={disabled}
              siblingCount={parameters.length}
            />
          </Tab.Pane>
        ))}
      </Tab.Content>
      <AsyncButton
        id={`${idRoute}-add-parameter`}
        onClick={handleAddParameter}
        className="d-flex align-items-center btn-sm btn-success"
        disabled={disabled}
      >
        <span className="me-2">Add parameter</span>
        <PlusCircle />
      </AsyncButton>
    </Tab.Container>
  );
};

AnalysisMethodParametersControl.displayName = "AnalysisMethodParametersControl";
